import TagManager from "react-gtm-module";
import * as Sentry from "@sentry/react";

import { eventNameToPayloadSchema, SENSITIVE_WORDS } from "@/config/analytics/constants";
import { eventNameSchema } from "@/config/analytics/schemas";
import type { EventName, EventPayload } from "@/config/analytics/types";
import { env } from "@/env";
import { getMetaAttributionStoreState } from "@/stores";

const getMetaAttributionParams = (): Record<string, string> => {
  const { fbc, fbclid, fbp } = getMetaAttributionStoreState();

  return {
    ...(fbp ? { fbp } : {}),
    ...(fbc ? { fbc } : {}),
    ...(fbclid ? { fbclid } : {}),
  };
};

export const createSensitiveWordsPattern = () => {
  return new RegExp(SENSITIVE_WORDS.join("|"), "gi");
};

const sensitiveWordsPattern = createSensitiveWordsPattern();

// Does not sanitize values that are not strings, since we don't want to accidentally break GTM tags that rely on non-string parameters by sanitizing them.
// Nested objects aren't sanitized either, but GTM also doesn't support them so this shouldn't be an issue in practice.
const sanitizeParams = (params: Record<string, unknown>): Record<string, unknown> => {
  return Object.fromEntries(
    Object.entries(params).map(([key, value]) => {
      return [
        key,
        typeof value === "string" ? value.replace(sensitiveWordsPattern, "[REDACTED]") : value,
      ];
    }),
  );
};

// Must use a regular function (not arrow) so `arguments` is preserved —
// GTM detects Consent Mode v2 commands by checking for an Arguments object.
function pushToDataLayer(..._args: unknown[]) {
  window.dataLayer = window.dataLayer || [];
  //eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

const init = () => {
  if (env.VITE_APP_ENV === "local") {
    pushToDataLayer("consent", "default", {
      analytics_storage: "granted",
      ad_storage: "granted",
      ad_user_data: "granted",
      ad_personalization: "granted",
      functionality_storage: "granted",
      personalization_storage: "granted",
      security_storage: "granted",
      wait_for_update: 0,
    });

    // Persistent flag — GTM's GA4 Configuration tag reads this and routes hits
    // to GA4 DebugView, exposing the full parameter list for every event.
    pushToDataLayer({ debug_mode: true });
  }

  TagManager.initialize({
    gtmId: env.VITE_GTM_CONTAINER_ID,
  });
};

const track = <T extends EventName>(
  eventName: T,
  ...args: EventPayload<T> extends undefined ? [] : [EventPayload<T>]
): void => {
  const eventNameWithPrefix = `${env.VITE_GTM_EVENT_PREFIX}${eventName}`;

  const eventValidation = eventNameSchema.safeParse(eventNameWithPrefix);
  if (!eventValidation.success) {
    Sentry.withScope((scope) => {
      scope.setTag("event_name", eventName);
      Sentry.captureException(eventValidation.error);
    });

    return;
  }

  const [payload] = args;

  const schema = eventNameToPayloadSchema[eventName];
  let eventParams: Record<string, unknown> | undefined;

  if (schema) {
    const validation = schema.safeParse(payload);

    if (!validation.success) {
      Sentry.withScope((scope) => {
        scope.setTag("event_name", eventName);
        Sentry.captureException(validation.error);
      });

      return;
    }

    eventParams = sanitizeParams(validation.data);
  }

  const combinedParams = { ...eventParams, ...getMetaAttributionParams() };

  TagManager.dataLayer({
    dataLayer: {
      ...combinedParams,
      event: eventNameWithPrefix,
    },
  });

  // Clean up parameters after sending the event, to prevent them from being included in subsequent events by GTM tags that don't specify their own parameters.
  if (Object.keys(combinedParams).length > 0) {
    TagManager.dataLayer({
      dataLayer: Object.fromEntries(
        Object.keys(combinedParams).map((key) => {
          return [key, undefined];
        }),
      ),
    });
  }
};

export const analytics = {
  track,
  init,
};
